import { defineStore, storeToRefs } from "pinia";
import { computed, ref, watch } from "vue";
import { useRoute } from "vue-router";

import { useAllSavedSearches } from "@/composition/api/savedSearches.ts";
import useFilterItem from "@/composition/archive/useFilterItem.ts";
import useFilterTermSet from "@/composition/archive/useFilterTermSet.ts";
import type { FilterRule } from "@/types/content.d.ts";
import { FilterField, FilterType, RelativeDateRange } from "@/types/enums.ts";
import type { rawFilterSet } from "@/types/ui.ts";

import useFilterSelectorStore from "./filterSelector.ts";
import useQuerySearchStore from "./querySearch.ts";

/**
 *  This function helps mapping values of the flat filter rules value into the format needed by the stores to display the
 *  correct selected Filters in the dropdown, it uses the filter term sets and find matches and maps these in a result that could
 *  be handled by the respective filter change functions, we might find a way to avoid that in the future but for now it's the only way
 *  to maintain the functionality
 */
export function mapFilterRulesToTermSet(
  type: FilterType,
  values: string[],
  termSets: rawFilterSet | null,
  metaDataTags?: string[] | null,
) {
  if (!termSets) return [];

  // added in NEWS-658 as additional field for saved searches, this uses the filter key instead of the values to identify the indivdual filters
  // the values filter is needed for backwards compatibility
  if (metaDataTags) {
    return termSets[type]
      .filter((termSet) => metaDataTags.includes(termSet.key))
      .map((termSet) => termSet.key);
  }

  return termSets[type]
    .filter((termSet) => termSet.terms.every((term) => values.includes(term)))
    .map((termSet) => termSet.key);
}

const useSavedSearchesStore = defineStore("search:savedSearches", () => {
  const route = useRoute();
  const filterSelectorStore = useFilterSelectorStore();
  const {
    data: internalSavedSearches,
    isFinished: savedSearchesCallFinished,
    isFetching: savedSearchCallIsFetching,
    onFetchFinally: finallyFetchedSavedSearches,
    execute: getSavedSearches,
    error: savedSearchesError,
  } = useAllSavedSearches();

  const {
    selectedMediaTypes,
    mediaTypeFilter,
    selectedGenres,
    genreFilter,
    selectedUrgencies,
    urgencyFilter,
    statusTagsFilter,
    selectedStatusTags,
    selectedCategories,
    categoryFilter,
    productFilter,
    selectedProducts,
    userNeedFilter,
    selectedUserNeeds,
    tShirtSizeFilter,
    selectedTShirtSizes,
    profileFilter,
    selectedProfiles,
  } = storeToRefs(filterSelectorStore);

  const queryStringStore = useQuerySearchStore();

  const { handleFilterChange: handleProductFilterChange } = useFilterItem(
    productFilter,
    selectedProducts,
  );

  const { handleFilterChange: handleMediaTypeFilterChange } = useFilterItem(
    mediaTypeFilter,
    selectedMediaTypes,
  );
  const { handleFilterChange: handleGenreFilterChange } = useFilterItem(
    genreFilter,
    selectedGenres,
  );
  const { handleFilterChange: handleUrgencyFilterChange } = useFilterItem(
    urgencyFilter,
    selectedUrgencies,
  );
  const { handleFilterChange: handleStatusTagsFilterChange } = useFilterItem(
    statusTagsFilter,
    selectedStatusTags,
  );
  const { handleFilterChange: handleCategoryFilterChange } = useFilterItem(
    categoryFilter,
    selectedCategories,
  );

  const { handleFilterChange: handleUserneedFilterChange } = useFilterItem(
    userNeedFilter,
    selectedUserNeeds,
  );

  const { handleFilterChange: handleTShirtSizeFilterChange } = useFilterItem(
    tShirtSizeFilter,
    selectedTShirtSizes,
  );

  const { handleFilterChange: handleProfileFilterChange } = useFilterItem(
    profileFilter,
    selectedProfiles,
  );

  type FilterMapType = Record<
    Exclude<
      FilterType,
      FilterType.QUERY | FilterType.URN | FilterType.FULLTEXT | FilterType.AREA
    >,
    (filters: string[], metaDataTags?: string[] | null) => void
  > & {
    [FilterType.QUERY]: (searchQueryString: string[]) => Promise<void>;
  };

  const filterTermSet = useFilterTermSet();

  const filterMap: FilterMapType = {
    [FilterType.PRODUCT]: (
      productFilters: string[],
      metaDataTags?: string[] | any,
    ) => {
      const splitProducts = mapFilterRulesToTermSet(
        FilterType.PRODUCT,
        productFilters,
        filterTermSet.value,
        metaDataTags,
      );
      splitProducts.forEach((product) => handleProductFilterChange(product));
    },
    [FilterType.URGENCY]: (
      urgencyFilters: string[],
      metaDataTags?: string[] | null,
    ) => {
      const mappedUrgencies = mapFilterRulesToTermSet(
        FilterType.URGENCY,
        urgencyFilters,
        filterTermSet.value,
        metaDataTags,
      );
      mappedUrgencies.forEach((urgency) => handleUrgencyFilterChange(urgency));
    },
    [FilterType.MEDIATYPE]: (
      mediaTypeFilters: string[],
      metaDataTags?: string[] | null,
    ) => {
      const mappedMediaTypes = mapFilterRulesToTermSet(
        FilterType.MEDIATYPE,
        mediaTypeFilters,
        filterTermSet.value,
        metaDataTags,
      );
      mappedMediaTypes.forEach((mediaType) =>
        handleMediaTypeFilterChange(mediaType),
      );
    },
    [FilterType.GENRE]: (
      genreFilters: string[],
      metaDataTags?: string[] | null,
    ) => {
      const mappedGenres = mapFilterRulesToTermSet(
        FilterType.GENRE,
        genreFilters,
        filterTermSet.value,
        metaDataTags,
      );
      mappedGenres.forEach((genre) => handleGenreFilterChange(genre));
    },
    [FilterType.STATUS_TAGS]: (
      statusTagsFilters: string[],
      metaDataTags?: string[] | null,
    ) => {
      const mappedStatusTags = mapFilterRulesToTermSet(
        FilterType.STATUS_TAGS,
        statusTagsFilters,
        filterTermSet.value,
        metaDataTags,
      );
      mappedStatusTags.forEach((statusTag) =>
        handleStatusTagsFilterChange(statusTag),
      );
    },
    [FilterType.CATEGORY]: (
      categoryFilters: string[],
      metaDataTags?: string[] | null,
    ) => {
      const mappedCategories = mapFilterRulesToTermSet(
        FilterType.CATEGORY,
        categoryFilters,
        filterTermSet.value,
        metaDataTags,
      );
      mappedCategories.forEach((category) =>
        handleCategoryFilterChange(category),
      );
    },
    [FilterType.QUERY]: async (searchQueryString: string[]) => {
      queryStringStore.searchQuery = searchQueryString.join(" ");
    },
    [FilterType.USER_NEED]: (
      userNeeds: string[],
      metaDataTags?: string[] | null,
    ) => {
      const mappedUserNeeds = mapFilterRulesToTermSet(
        FilterType.USER_NEED,
        userNeeds,
        filterTermSet.value,
        metaDataTags,
      );
      mappedUserNeeds.forEach((userNeed) => {
        handleUserneedFilterChange(userNeed);
      });
    },
    [FilterType.T_SHIRT_SIZE]: (
      tShirtSizes: string[],
      metaDataTags?: string[] | null,
    ) => {
      const mappedTShirtSizes = mapFilterRulesToTermSet(
        FilterType.T_SHIRT_SIZE,
        tShirtSizes,
        filterTermSet.value,
        metaDataTags,
      );
      mappedTShirtSizes.forEach((tShirtSize) =>
        handleTShirtSizeFilterChange(tShirtSize),
      );
    },
    [FilterType.FILTER_DPA_IQ_PROFILE_ID]: (
      profileFilters: string[],
      metaDataTags?: string[] | null,
    ) => {
      const splitProfiles = mapFilterRulesToTermSet(
        FilterType.FILTER_DPA_IQ_PROFILE_ID,
        profileFilters,
        filterTermSet.value,
        metaDataTags,
      );
      splitProfiles.forEach((profile) => handleProfileFilterChange(profile));
    },
    [FilterType.DATE]: (newDate: string[]) => {
      const convertedDate: [Date, Date] = [
        new Date(newDate[0]),
        new Date(newDate[1]),
      ];
      filterSelectorStore.setSelectedDate(convertedDate, false);
    },
    [FilterType.RELATIVE_DATE]: (newRelativeDate: string[]) => {
      filterSelectorStore.setSelectedRelativeDate(
        newRelativeDate as [RelativeDateRange, string],
        false,
      );
    },
  };

  const activeSavedSearchId = ref<string | null>(null);

  function resetActiveSavedSearchId() {
    if (activeSavedSearchId.value) {
      activeSavedSearchId.value = null;
    }
  }

  function setSelectedSearch(filterRules: FilterRule[], ssid: string) {
    filterSelectorStore.resetFilters();
    queryStringStore.resetSearchQuery();
    activeSavedSearchId.value = ssid;
    filterRules.forEach((rule) => {
      if (!rule.type || rule.exclude) return;

      const filterType = rule.type as keyof typeof filterMap;
      if (!(filterType in filterMap)) return;

      if (rule.type === FilterType.QUERY) {
        if (rule.fields && rule.fields.length > 0) {
          queryStringStore.setFilterFields(rule.fields as FilterField[]);
        }
      }
      if (!rule.values) return;
      filterMap[filterType](rule.values, rule.metaDataTags);
    });
  }

  const savedSearches = computed(() => {
    return (
      internalSavedSearches.value?.map((savedSearch) => {
        return {
          ...savedSearch,
          selected: activeSavedSearchId.value === savedSearch.id,
        };
      }) ?? []
    );
  });

  const savedSearchesCount = computed(() => {
    return savedSearches.value?.length ?? 0;
  });

  watch(
    () => route.query["savedSearchId"],
    (newSavedSearchId) => {
      if (newSavedSearchId) {
        activeSavedSearchId.value = newSavedSearchId as string;
      }
    },
    { immediate: true },
  );

  return {
    activeSavedSearchId,
    savedSearches,
    savedSearchesCount,
    savedSearchCallIsFetching,
    savedSearchesCallFinished,
    getSavedSearches,
    finallyFetchedSavedSearches,
    setSelectedSearch,
    savedSearchesError,
    resetActiveSavedSearchId,
  };
});

export default useSavedSearchesStore;
